import { useState, useEffect, useCallback, useRef } from 'react'
import { listIoTThings, loadDeviceShadows } from "../services/machines/machinesApi";
import { DeviceThing } from "../types/DeviceThing.types";
import { useAppContext } from "../context/AppContext";
import { describeError } from '../services/machines/errors'

// Background results are applied in batches: one list re-render per batch instead of one per shadow.
const BATCH_DELAY_MS = 100

export function useFetchIoTDevices() {
	const [machines, setMachines] = useState<DeviceThing[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null)
	const visible = useRef<ReadonlySet<string>>(new Set())
	const { awsInstanceIot, awsInstanceIotData } = useAppContext();
	// Rows on screen are loaded first; a ref, so scrolling or paging never restarts the loading.
	const setVisibleMachines = useCallback((thingNames: string[]) => {
		visible.current = new Set(thingNames)
	}, [])
	useEffect(() => {
		const controller = new AbortController()
		const { signal } = controller
		let batch = new Map<string, DeviceThing>()
		let timer: ReturnType<typeof setTimeout> | undefined
		const flush = () => {
			const loaded = batch
			batch = new Map()
			timer = undefined
			setMachines((previous) => previous.map((machine) => loaded.get(machine.thingName!) ?? machine))
		}
		setMachines([])
		setError(null)
		if (!awsInstanceIot || !awsInstanceIotData) {
			setIsLoading(false);
			setError('Brak aktywnej sesji AWS. Zaloguj się ponownie.')
			return;
		}
		setIsLoading(true)
		listIoTThings(awsInstanceIot, signal)
			.then((things) => {
				setMachines(things)
				setIsLoading(false)
				return loadDeviceShadows(
					things,
					awsInstanceIotData,
					(device) => {
						batch.set(device.thingName!, device)
						timer ??= setTimeout(flush, BATCH_DELAY_MS)
					},
					{ signal, isVisible: (thingName) => visible.current.has(thingName) }
				)
			})
			.catch((error) => {
				if (!signal.aborted) setError(describeError(error))
			})
			.finally(() => {
				if (!signal.aborted) setIsLoading(false);
			})
		return () => {
			controller.abort()
			clearTimeout(timer)
		}
	}, [awsInstanceIot, awsInstanceIotData])
	return { machines, isLoading, error, setVisibleMachines }
}
